import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import CapitalExpenditureLineChart from "../chart/CapitalExpenditureLineChart";
import CapitalExpenditureEfficiencyLineChart from "../chart/CapitalExpenditureEfficiencyLineChart";
import CommonDividendsPaidLineChart from "../chart/CommonDividendsPaidLineChart";
import CommonStockRepurchasedLineChart from "../chart/CommonStockRepurchasedLineChart";
import NetIncomeOperatingCashFlowLineChart from "../chart/NetIncomeOperatingCashFlowLineChart";
import OperatingCashFlowBreakdownChart from "../chart/OperatingCashFlowBreakdownChart";
import OperatingCashFlowAdjustmentsBarChart from "../chart/OperatingCashFlowAdjustmentsBarChart";
import WorkingCapitalDetailsBarChart from "../chart/WorkingCapitalDetailsBarChart";
import { fetchCashFlowStatementData } from "../../apis/Stock";

export default function CashFlow({ ticker }) {
  const [selectedPeriod, setSelectedPeriod] = useState("quarter");
  const [isLoading, setIsLoading] = useState(false);
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });

  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await fetchCashFlowStatementData(ticker, selectedPeriod);

        if (!response.ok) {
          throw new Error("Failed to fetch cash flow statement");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching cash flow statement:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  return (
    <div className="w-100 h-100" style={{ minHeight: "300px" }}>
      <div className="d-flex justify-content-start align-items-center mb-2 ps-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="현금흐름 차트 기간 선택">
          <button
            type="button"
            className={`btn ${selectedPeriod === "quarter" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setSelectedPeriod("quarter")}
          >
            분기
          </button>
          <button
            type="button"
            className={`btn ${selectedPeriod === "annual" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setSelectedPeriod("annual")}
          >
            연간
          </button>
        </div>
      </div>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <NetIncomeOperatingCashFlowLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <OperatingCashFlowAdjustmentsBarChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <WorkingCapitalDetailsBarChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <OperatingCashFlowBreakdownChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <CapitalExpenditureLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <CapitalExpenditureEfficiencyLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
      <Row style={{ margin: "0 auto" }}>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <CommonStockRepurchasedLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-2">
          <CommonDividendsPaidLineChart ticker={ticker} selectedPeriod={selectedPeriod} chartDataByPeriod={chartDataByPeriod} isLoading={isLoading} />
        </Col>
      </Row>
    </div>
  );
}
